import { useState } from 'react';
import { CloseOutlined } from '@ant-design/icons';

import { Button, Checkbox, Dialog, Field, Skeleton, Spinner } from '../../../components/ui';
import { useLoi } from '../../../hooks/useLoi';
import { useT } from '../../../i18n';
import { soVN } from '../../../lib/dinh-dang';
import { CayChonPhongBan } from '../../co-cau-to-chuc/components/CayChonPhongBan';
import { phangTheoCay } from '../../co-cau-to-chuc/nghiep-vu/cay';
import { LoiTrongHop } from '../../tenant/components/phan-form';
import {
  useDatNguoiTep,
  useDatPhongBanTep,
  useDatQuyenThuMuc,
  useNguoiTheoPhongBan,
  usePhongBan,
  useQuyenNguoiTep,
  useQuyenThuMuc,
} from '../hooks/useTriThuc';
import type { Folder, KnowledgeFile, NguoiDuocDung } from '../types';

export type DoiTuongQuyen = { loai: 'thuMuc'; thuMuc: Folder } | { loai: 'tep'; tep: KnowledgeFile };

/**
 * Chọn người được dùng một thư mục hay được đọc một tệp. Người chọn theo từng phòng ban; người đã chọn ở
 * phòng ban khác vẫn giữ. Đọc danh sách hiện tại trước, rồi mới dựng phần chọn — để trạng thái khởi tạo
 * đúng một lần từ dữ liệu, không đồng bộ bằng `useEffect`.
 */
export function HopNguoiDuocDung({
  kbId,
  doiTuong,
  open,
  onClose,
}: {
  kbId: string;
  doiTuong: DoiTuongQuyen;
  open: boolean;
  onClose: () => void;
}) {
  const { t } = useT();
  const dichLoi = useLoi();
  const laThuMuc = doiTuong.loai === 'thuMuc';
  const quyenThuMuc = useQuyenThuMuc(kbId, open && laThuMuc);
  const quyenTep = useQuyenNguoiTep(kbId, open && !laThuMuc);
  const q = laThuMuc ? quyenThuMuc : quyenTep;
  const ten = doiTuong.loai === 'thuMuc' ? doiTuong.thuMuc.name : doiTuong.tep.file_name;

  const hienTai: NguoiDuocDung[] | undefined = q.data
    ? (doiTuong.loai === 'thuMuc'
      ? quyenThuMuc.data?.items.find((x) => x.folder_id === doiTuong.thuMuc.id)?.permitted_users
      : quyenTep.data?.items.find((x) => x.file_id === doiTuong.tep.id)?.permitted_users) ?? []
    : undefined;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t(laThuMuc ? 'triThuc.quyen.thuMucTieuDe' : 'triThuc.quyen.nguoiTepTieuDe', { ten })}
      width={600}
      destroyOnHidden
    >
      {q.isLoading ? (
        <div className="flex justify-center py-12">
          <Spinner />
        </div>
      ) : q.isError || !hienTai ? (
        <div className="space-y-3 pt-2">
          <LoiTrongHop>{t('triThuc.quyen.loiDoc', { loi: dichLoi(q.error) })}</LoiTrongHop>
          <div className="flex justify-end gap-3">
            <Button variant="outline" size="md" onClick={onClose}>
              {t('chung.dong')}
            </Button>
            <Button variant="primary" size="md" onClick={() => void q.refetch()}>
              {t('chung.thuLai')}
            </Button>
          </div>
        </div>
      ) : (
        <ChonNguoi kbId={kbId} doiTuong={doiTuong} hienTai={hienTai} onClose={onClose} />
      )}
    </Dialog>
  );
}

function ChonNguoi({
  kbId,
  doiTuong,
  hienTai,
  onClose,
}: {
  kbId: string;
  doiTuong: DoiTuongQuyen;
  hienTai: NguoiDuocDung[];
  onClose: () => void;
}) {
  const { t, locale } = useT();
  const dichLoi = useLoi();
  const phongBan = usePhongBan();
  const datThuMuc = useDatQuyenThuMuc(kbId);
  const datTep = useDatNguoiTep();
  const dangLuu = datThuMuc.isPending || datTep.isPending;

  const [daChon, setDaChon] = useState<Map<string, string>>(
    () => new Map(hienTai.map((n) => [n.user_id, n.display_name ?? n.user_id])),
  );
  const [phong, setPhong] = useState<string | undefined>(hienTai[0]?.department_id ?? undefined);
  const [loi, setLoi] = useState<unknown>(null);
  const nguoi = useNguoiTheoPhongBan(phong);

  const bat = (id: string, tenNguoi: string, co: boolean) => {
    setDaChon((cu) => {
      const moi = new Map(cu);
      if (co) moi.set(id, tenNguoi);
      else moi.delete(id);
      return moi;
    });
    setLoi(null);
  };

  const dsPhong = nguoi.data ?? [];
  const daChonCaPhong = dsPhong.length > 0 && dsPhong.every((n) => daChon.has(n.user_id));

  const luu = async () => {
    setLoi(null);
    const ids = [...daChon.keys()];
    try {
      if (doiTuong.loai === 'thuMuc') await datThuMuc.mutateAsync({ thuMuc: doiTuong.thuMuc, userIds: ids });
      else await datTep.mutateAsync({ tep: doiTuong.tep, ids });
      onClose();
    } catch (err) {
      setLoi(err);
    }
  };

  return (
    <div className="space-y-4 pt-2">
      <p className="text-xs leading-relaxed text-content-muted">
        {t(doiTuong.loai === 'thuMuc' ? 'triThuc.quyen.thuMucMo' : 'triThuc.quyen.nguoiTepMo')}
      </p>

      <div className="space-y-2">
        <p className="text-sm font-medium text-content-secondary">
          {t('triThuc.quyen.daChon', { so: soVN(daChon.size, locale) })}
        </p>
        {daChon.size === 0 ? (
          <p className="text-xs text-content-muted">{t('triThuc.quyen.chuaChonAi')}</p>
        ) : (
          <ul className="flex max-h-24 flex-wrap gap-1.5 overflow-y-auto">
            {[...daChon].map(([id, tenNguoi]) => (
              <li key={id} className="inline-flex items-center gap-1 rounded border border-default bg-subtle py-0.5 pl-2 pr-1 text-xs">
                <span className="max-w-48 truncate text-content-primary">{tenNguoi}</span>
                <button
                  type="button"
                  className="rounded p-0.5 text-content-muted hover:bg-surface hover:text-danger-700"
                  title={t('triThuc.quyen.boNguoi', { ten: tenNguoi })}
                  aria-label={t('triThuc.quyen.boNguoi', { ten: tenNguoi })}
                  onClick={() => bat(id, tenNguoi, false)}
                >
                  <CloseOutlined aria-hidden className="text-2xs" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <Field
        label={t('triThuc.quyen.fPhongBan')}
        error={phongBan.isError ? t('triThuc.loi.phongBan', { loi: dichLoi(phongBan.error) }) : undefined}
      >
        {(p) => (
          <CayChonPhongBan
            fieldProps={p}
            ds={phongBan.data?.items ?? []}
            loading={phongBan.isLoading}
            placeholder={t('triThuc.kho.chonPhongBan')}
            khongCo={t('nguoiDung.fPhongBanKhongCo')}
            value={phong ?? null}
            onChange={(v) => setPhong(v ?? undefined)}
          />
        )}
      </Field>

      {phong && (
        <div className="rounded border border-default">
          {nguoi.isLoading ? (
            <div className="space-y-2 p-3">
              <Skeleton className="h-5 w-2/3" />
              <Skeleton className="h-5 w-1/2" />
              <Skeleton className="h-5 w-3/5" />
            </div>
          ) : nguoi.isError ? (
            <div role="alert" className="flex flex-wrap items-center justify-between gap-3 p-3">
              <span className="min-w-0 text-xs text-danger-700">{t('triThuc.loi.nguoi', { loi: dichLoi(nguoi.error) })}</span>
              <Button variant="outline" size="sm" onClick={() => void nguoi.refetch()}>
                {t('chung.thuLai')}
              </Button>
            </div>
          ) : dsPhong.length === 0 ? (
            <p className="p-3 text-xs text-content-muted">{t('triThuc.quyen.phongTrong')}</p>
          ) : (
            <>
              <div className="border-b border-default px-3 py-2">
                <Checkbox
                  checked={daChonCaPhong}
                  indeterminate={!daChonCaPhong && dsPhong.some((n) => daChon.has(n.user_id))}
                  onChange={(e) => {
                    for (const n of dsPhong) bat(n.user_id, n.display_name, e.target.checked);
                  }}
                >
                  <span className="text-sm font-medium text-content-primary">
                    {t('triThuc.quyen.chonCaPhong', { so: soVN(dsPhong.length, locale) })}
                  </span>
                </Checkbox>
              </div>
              <ul className="max-h-56 space-y-1 overflow-y-auto px-3 py-2">
                {dsPhong.map((n) => (
                  <li key={n.user_id}>
                    <Checkbox checked={daChon.has(n.user_id)} onChange={(e) => bat(n.user_id, n.display_name, e.target.checked)}>
                      <span className="text-sm text-content-primary">{n.display_name}</span>
                    </Checkbox>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}

      {Boolean(loi) && <LoiTrongHop>{dichLoi(loi)}</LoiTrongHop>}
      <div className="flex flex-wrap justify-end gap-3 border-t border-default pt-3">
        <Button variant="outline" size="md" onClick={onClose}>
          {t('chung.huy')}
        </Button>
        <Button variant="primary" size="md" loading={dangLuu} onClick={() => void luu()}>
          {dangLuu ? t('chung.dangLuu') : t('triThuc.quyen.luuNguoi', { so: soVN(daChon.size, locale) })}
        </Button>
      </div>
    </div>
  );
}

/**
 * Chọn phòng ban được đọc một tệp (`PUT /files/{id}/permissions`). Đã đặt rồi thì không quay về "theo bộ"
 * được (backend như native); bỏ chọn hết là không phòng ban nào đọc được.
 */
export function HopPhongBanTep({ tep, open, onClose }: { tep: KnowledgeFile; open: boolean; onClose: () => void }) {
  const { t, locale } = useT();
  const dichLoi = useLoi();
  const phongBan = usePhongBan();
  const dat = useDatPhongBanTep();
  const [chon, setChon] = useState<Set<string>>(() => new Set(tep.permitted_departments.map((p) => p.id)));
  const [loi, setLoi] = useState<unknown>(null);

  const luu = async () => {
    setLoi(null);
    try {
      await dat.mutateAsync({ tep, ids: [...chon] });
      onClose();
    } catch (err) {
      setLoi(err);
    }
  };

  const ds = phangTheoCay(phongBan.data?.items ?? [], locale);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t('triThuc.quyen.phongBanTepTieuDe', { ten: tep.file_name })}
      width={480}
      destroyOnHidden
      footer={
        <div className="space-y-3">
          {Boolean(loi) && <LoiTrongHop>{dichLoi(loi)}</LoiTrongHop>}
          <div className="flex flex-wrap justify-end gap-3">
            <Button variant="outline" size="md" onClick={onClose}>
              {t('chung.huy')}
            </Button>
            <Button variant="primary" size="md" loading={dat.isPending} disabled={!phongBan.data} onClick={() => void luu()}>
              {dat.isPending ? t('chung.dangLuu') : t('triThuc.quyen.luuPhongBan', { so: soVN(chon.size, locale) })}
            </Button>
          </div>
        </div>
      }
    >
      <div className="space-y-3 pt-2">
        <p className="text-xs leading-relaxed text-content-muted">
          {t(tep.dept_permissions_configured ? 'triThuc.quyen.phongBanTepMo' : 'triThuc.quyen.phongBanTepMoLanDau')}
        </p>
        {phongBan.isLoading ? (
          <div className="space-y-2">
            <Skeleton className="h-5 w-2/3" />
            <Skeleton className="h-5 w-1/2" />
          </div>
        ) : phongBan.isError ? (
          <div role="alert" className="flex flex-wrap items-center justify-between gap-3">
            <span className="min-w-0 text-xs text-danger-700">{t('triThuc.loi.phongBan', { loi: dichLoi(phongBan.error) })}</span>
            <Button variant="outline" size="sm" onClick={() => void phongBan.refetch()}>
              {t('chung.thuLai')}
            </Button>
          </div>
        ) : ds.length === 0 ? (
          <p className="text-xs text-content-muted">{t('triThuc.quyen.chuaCoPhongBan')}</p>
        ) : (
          <ul className="max-h-64 space-y-1 overflow-y-auto rounded border border-default px-3 py-2">
            {ds.map((p) => (
              <li key={p.id} style={{ paddingInlineStart: `${(p.cap - 1) * 20}px` }}>
                <Checkbox
                  checked={chon.has(p.id)}
                  onChange={(e) => {
                    setChon((cu) => {
                      const moi = new Set(cu);
                      if (e.target.checked) moi.add(p.id);
                      else moi.delete(p.id);
                      return moi;
                    });
                    setLoi(null);
                  }}
                >
                  <span className="text-sm text-content-primary">{p.name}</span>
                </Checkbox>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Dialog>
  );
}
